

import {ToolbarService} from "@/services/toolbar-service";
import AppToProcessFiles from "@/components/inbox/ToProcessFiles.vue";
import AppInboxSplitView from "@/components/inbox/SplitView.vue";
import AppPeppolRegistrationModal from "@/components/inbox/PeppolRegistrationModal.vue";
import AppEmailPreview from "@/components/inbox/EmailPreview.vue";
import AppEditPurchase from "@/views/purchase/PurchaseEdit.vue";
import Vue from "vue";
import {InboxState} from "@/utils/inbox/inbox-state";
import {InboxreaderPurchase} from "@/utils/purchase/inboxreader-purchase";
import {AuthService, AWSFileService, DossierConfigService, PurchaseService, SaleService, UserConfigService} from "@/services";
import {Permissions} from "@/models/user-configs/permissions";
import AppSendMessageDialog from "@/components/mail/SendMessageDialog.vue";
import {IPeppolRegistration, PeppolService} from "@/services/peppol-service";
import { copyToClipboard } from "@/utils/clipboard";
import { MODULE } from "@/config/modules";
import {Tier} from "@/utils/payments/tiers";
import {ConfirmResult, ISelectItem} from "winfakt-vue-components";
import { Purchase } from "@/models/base";
import { boolToNumber } from "@/utils/numbers";
import { HttpError } from "@/utils/services/http-error";
import { TranslateResult } from "vue-i18n";
import { InboxreaderService } from "@/services/inboxreader-service";
import { InboxreaderPurchaseFile } from "@/utils/purchase/inboxreader-purchase-file";
import { InboxreaderLabel } from "@/utils/purchase/inboxreader-label";
import { getLabelTextColor, labelItemHtml } from "@/utils/inbox/label-color";
import AppInboxLabelsModal from "@/components/inbox/InboxLabelsModal.vue";
import AppUploadPurchaseModal from "@/components/inbox/UploadPurchaseModal.vue";
import AppUploadQueueBar from "@/components/inbox/UploadQueueBar.vue";
import { InboxUpload } from "@/utils/inbox/inbox-upload-job";
import { InboxBarPosition, InboxTab, TAB_SOURCES } from "@/utils/inbox/inbox-state";
import { INBOX_SOURCES, InboxSource } from "@/utils/inbox/inbox-list-state";

export default Vue.extend({
	data() {
		return {
			state: new InboxState(this),
			creating: false,
			bulkProcessing: false,
			dossierConfig: AuthService.wfDossier,
			showRegistration: false,
			sendMailDialogOpened: false,
			downloadedFiles: [] as File[],
			mailSubject: "",
			mailTo: "",
			mailContent: "",
			settings: DossierConfigService.getPluginConfig(),
			registrations: [] as IPeppolRegistration[],
			searchTimeout: null as ReturnType<typeof setTimeout> | null,
			noteForBookkeeperFile: null as null | InboxreaderPurchaseFile,
			showLabelsModal: false,
			showUploadModal: false,
			uploadJob: InboxUpload,
			labelsExpanded: false,
			labelFilterLimit: 5
		};
	},
	computed: {
		orderedFilterLabels(): InboxreaderLabel[] {
			let selected = this.state.labels.filter(l=>this.state.selectedLabelIds.includes(l.ID));
			let rest = this.state.labels.filter(l=>!this.state.selectedLabelIds.includes(l.ID));
			return [...selected, ...rest];
		},
		visibleFilterLabels(): InboxreaderLabel[] {
			if (this.labelsExpanded) return this.orderedFilterLabels;
			return this.orderedFilterLabels.slice(0, this.labelFilterLimit);
		},
		hiddenLabelCount(): number {
			return Math.max(0, this.orderedFilterLabels.length - this.labelFilterLimit);
		},
		isOwner(): boolean {
			return AuthService.wfCompany.OwnerID == AuthService.wfUser.ID;
		},
		// visibleTabs are filters on the one list: everything, only peppol or
		// only mail and upload. Uploading needs no module, receiving mail does,
		// so the third tab is named after what can end up in it.
		visibleTabs(): {value:InboxTab, text:string, icon:string|string[], count:number, showCount:boolean}[] {
			let hasMail = this.hasModulePurchaseInbox;
			let otherText = hasMail ? this.$ct("tab-email-upload") : this.$ct("tab-upload");
			let otherIcon:string|string[] = hasMail ? ["far", "envelope"] : "cloud-upload-alt";
			let tabs:{value:InboxTab, text:string, icon:string|string[]}[] = [
				{value: "all", text: this.$ct("tab-all").toString(), icon: ["far", "envelope-open"]},
				{value: "peppol", text: this.$ct("tab-peppol").toString(), icon: "share-alt"},
				{value: "other", text: otherText.toString(), icon: otherIcon}
			];
			return tabs.map(t=>({
				...t,
				count: this.state.listState.countOf(TAB_SOURCES[t.value]),
				showCount: this.state.listState.countsLoaded
			}));
		},
		// showsPeppolDocuments is true whenever the visible list can contain
		// peppol documents.
		showsPeppolDocuments(): boolean {
			return this.state.tab != "other";
		},
		// The chips narrow the mixed list down; the other tabs are already one
		// source so there is nothing to narrow there.
		showSourceChips(): boolean {
			return this.state.tab == "all";
		},
		sourceFilters(): {value:InboxSource, text:string, active:boolean}[] {
			return INBOX_SOURCES.map(source=>({
				value: source,
				text: this.$ct(source == "peppol" ? "source-peppol" : "source-other").toString(),
				active: this.state.listState.isSourceActive(source)
			}));
		},
		// A pinned bar stays put even without a selection, so the list does not
		// jump up and down while selecting.
		showSelectionBar(): boolean {
			if (this.state.view == "split") return false;
			return this.selectionCount > 0 || this.state.barPosition == "pinned";
		},
		selectionCount(): number {
			return this.state.selectedType.selection.length;
		},
		// Forwarding several documents at once only works for peppol documents;
		// for anything else the mail dialog handles one document at a time.
		canForwardSelection(): boolean {
			if (!this.selectionCount) return false;
			if (this.selectionHasUnapprovedFiles) return false;
			if (this.selectionCount == 1) return true;
			let everyIsPeppol = this.state.selectedType.selection.every(p=>p.PeppolOrder != null);
			return everyIsPeppol && !!this.peppolBookkeeperEmail;
		},
		selectionIsMixed(): boolean {
			return this.state.listState.hasMixedSelection();
		},
		isSelfBillingItemSelected():boolean{
			if (this.state.selectedType.selection.length != 1) return false;
			if (!this.state.selectedType.selection[0].PeppolOrder) return false;
			if (!this.state.selectedType.selection[0].PeppolOrder.isSelfBilling()) return false;
			return true;
		},
		buttonText(): TranslateResult {
			return this.state.selectedType.selection.length > 1 ? this.$ct("create-group-purchase") : this.$ct("create-purchase");
		},
		showToProcessFiles(): boolean {
			// Only the peppol tab of a dossier that is not registered yet has
			// nothing to show but the registration message.
			if (this.state.tab != "peppol") return true;
			return this.dossierConfig.peppolRegistrationStatus == 2;
		},
		peppolMsg(): string {
			return this.$ct(this.dossierConfig.peppolRegistrationStatus == 0 ? "peppol-msg-1" : "peppol-msg-2").toString();
		},
		isAdmin(): boolean {
			return UserConfigService.hasCurrentUserPermission((permissions: Permissions): boolean => {
				return permissions.IsAdmin;
			});
		},
		// Uploading is there for every dossier; only the peppol tab has nothing
		// to do with it.
		canUpload(): boolean {
			if (this.state.archive) return false;
			return this.state.tab != "peppol";
		},
		hasModulePurchaseInbox(): boolean {
			return AuthService.hasModule(MODULE.INKOMENDE_AANKOPEN_UIT_MAILBOX);
		},
		hasSelfBillingModule():boolean {
			return AuthService.hasModule(MODULE.SELF_BILLING);
		},
		isFree(): boolean {
			return AuthService.wfCompany.Tier == Tier.FREE;
		},
		archiveItems(): ISelectItem<boolean>[] {
			let result:ISelectItem<boolean>[] = [];
			result.push({
				text: "Openstaande documenten",
				value: false
			});
			result.push({
				text: "Gearchiveerde documenten",
				value: true
			});
			return result;
		},
		displayedPeppolId():string {
			let registrations = [...this.registrations].sort((a,b)=>boolToNumber(a.SendOnly) - boolToNumber(b.SendOnly));
			for (let registration of registrations) {
				if (registration.PeppolID.startsWith("0208")){
					return registration.PeppolID;
				}
			}
			if (registrations.length > 0){
				return registrations[0].PeppolID;
			}
			return "";
		},
		displayedPeppolIdIsSendOnly():boolean{
			let registrations = [...this.registrations].sort((a,b)=>boolToNumber(a.SendOnly) - boolToNumber(b.SendOnly));
			for (let registration of registrations) {
				if (registration.PeppolID.startsWith("0208")){
					return registration.SendOnly;
				}
			}
			if (registrations.length > 0){
				return registrations[0].SendOnly;
			}
			return false;
		},
		canApprove():boolean{
			const purchaseConfig = DossierConfigService.getPurchaseConfig();
			if (!purchaseConfig.RequireApprovalBeforeProcessing) return false;
			if (this.isOwner) return true;
			return UserConfigService.hasCurrentUserPermission((permissions: Permissions): boolean => {
				return permissions.CanApproveInboxPurchases;
			});
		},
		selectionHasUnapprovedFiles():boolean{
			const purchaseConfig = DossierConfigService.getPurchaseConfig();
			if (!purchaseConfig.RequireApprovalBeforeProcessing) return false;
			for (const purchase of this.state.selectedType.selection) {
				for (const file of purchase.Files) {
					if (!file.Approved) return true;
				}
			}
			return false;
		},
		selectionHasApprovedFiles():boolean{
			const purchaseConfig = DossierConfigService.getPurchaseConfig();
			if (!purchaseConfig.RequireApprovalBeforeProcessing) return false;
			for (const purchase of this.state.selectedType.selection) {
				for (const file of purchase.Files) {
					if (file.Approved) return true;
				}
			}
			return false;
		},
		barPositionItems(): ISelectItem<InboxBarPosition>[]{
			return [
				{text: this.$ct("bar-position-top").toString(), value: "top", icon: this.state.barPosition == "top" ? "check" : undefined},
				{text: this.$ct("bar-position-bottom").toString(), value: "bottom", icon: this.state.barPosition == "bottom" ? "check" : undefined},
				{text: this.$ct("bar-position-pinned").toString(), value: "pinned", icon: this.state.barPosition == "pinned" ? "check" : undefined}
			];
		},
		labelMenuItems(): ISelectItem<number>[]{
			return this.state.labels.map(l=>({text: labelItemHtml(l), value: l.ID}));
		},
		peppolBookkeeperEmail(): string {
			let configs = this.settings.UBL.BookkeeperEmailConfigs;
			return configs["incomming-peppol-orders"]?.Email || configs["purchases"]?.Email || "";
		},
	},
	watch: {
		async "state.archive"(){
			await this.state.fetchPurchases();
		},
		"state.selectedLabelIds": {
			deep: true,
			async handler(){
				await this.state.fetchPurchases();
			}
		},
		"state.search": {
			handler(){
				if (this.searchTimeout) clearTimeout(this.searchTimeout);
				this.searchTimeout = setTimeout(async () => {
					await this.state.fetchPurchases();
				}, 300);
			}
		},
	},
	methods: {
		async downloadSelection(){
			let selection = this.state.selectedType.selection;
			if (!selection.length) return;
			this.bulkProcessing = true;
			try {
				let singleFile = selection.length == 1 && !selection[0].PeppolOrder ? selection[0].Files[0] : null;
				if (singleFile && singleFile.File) {
					await AWSFileService.downloadFile(singleFile.File.Token, singleFile.File.Name);
					return;
				}
				let purchaseIds = [...new Set(selection.map(p=>p.ID))];
				let fileIds = selection.filter(p=>!p.PeppolOrder).flatMap(p=>p.Files.map(f=>f.ID));
				await InboxreaderService.downloadZip(purchaseIds, fileIds);
			} catch (err) {
				this.notifyError(err);
			} finally {
				this.bulkProcessing = false;
			}
		},
		async forwardSelection(){
			let selection = this.state.selectedType.selection;
			if (!selection.length) return;
			if (selection.length == 1) {
				await this.openSendModal(selection[0]);
				return;
			}
			this.bulkProcessing = true;
			try {
				await this.bulkSendToBookkeeper();
			} catch (err) {
				this.notifyError(err);
			} finally {
				this.bulkProcessing = false;
			}
		},
		async archiveSelection(){
			let selection = this.state.selectedType.selection;
			if (!selection.length) return;
			let message = this.$ct("confirm-archive-selection", {count: selection.length});
			let confirm = await this.$wf.confirm(message, this.$ct("common.yes"), this.$ct("common.no"), this.$ct("common.warning"), "warning");
			if (!confirm) return;
			this.bulkProcessing = true;
			try {
				for (let entry of selection) {
					// A peppol document is archived as a whole, of a mail only
					// the selected attachments.
					if (entry.PeppolOrder) {
						await InboxreaderService.archiveInboxreaderPurchase(entry.ID);
						continue;
					}
					for (let file of entry.Files) {
						await InboxreaderService.archiveInboxreaderPurchaseFile(entry.ID, file.ID);
					}
				}
				this.state.clearSelection();
				await this.state.fetchPurchases();
			} catch (err) {
				this.notifyError(err);
			} finally {
				this.bulkProcessing = false;
			}
		},
		notifyError(err:unknown){
			let hErr = err as HttpError;
			this.$wf.notify(hErr.getText ? hErr.getText() : String(err), "danger", 5000, "exclamation-triangle");
		},
		async toggleSource(source:InboxSource){
			this.state.listState.toggleSource(source);
			this.state.clearSelection();
			await this.state.fetchPurchases();
		},
		async setTab(tab:InboxTab){
			await this.state.setTab(tab);
		},
		copy(info:string){
			copyToClipboard(info);
			this.$wf.notify(this.$ct("copied"), "success", 3000, "check");
		},
		// eslint-disable-next-line max-lines-per-function
		async getFilesToAttach(p: InboxreaderPurchase): Promise<File[]> {
			let attachmentsConfig = this.settings.UBL.BookkeeperEmailConfigs["purchases"]?.Attachments || [];
			let allowedExtensions: string[] = [];
			if (attachmentsConfig.includes("pdf")) {
				allowedExtensions.push(".pdf");
			}
			let usesPeppolXml = attachmentsConfig.includes("peppol");
			if (attachmentsConfig.includes("ubl") || usesPeppolXml || attachmentsConfig.includes("xml")) {
				allowedExtensions.push(".xml");
			}
			let peppolXmlIncluded = false;
			let filesToAttach: File[] = [];
			for (let file of p.Files) {
				if (!file.File) continue;
				let { Token, Name, ContentType } = file.File;
				let lowerName = Name.toLowerCase();
				let hasAllowedExtension = false;
				// Controleer eerst content type, daarna extensie
				if (ContentType === "application/pdf" && allowedExtensions.includes(".pdf")) {
					if (!Name.endsWith(".pdf")){
						Name = Name + ".pdf";
					}
					hasAllowedExtension = true;
				} else if ((ContentType === "application/xml" || ContentType === "text/xml") && allowedExtensions.includes(".xml")) {
					if (!Name.endsWith(".xml")){
						Name = Name + ".xml";
					}
					hasAllowedExtension = true;
				}else{
					hasAllowedExtension = allowedExtensions.some(ext => lowerName.endsWith(ext));
				}

				if (hasAllowedExtension) {
					if (usesPeppolXml && !peppolXmlIncluded && ContentType.endsWith("xml") && Name.endsWith("-UBL.xml")) {
						var fetched = await InboxreaderService.downloadUblForBookkeeper(p.ID);
						peppolXmlIncluded = true;
					}else{
						var fetched = await AWSFileService.fetchFile(Token, Name, ContentType);
					}
					filesToAttach.push(fetched);
				}
			}
			return filesToAttach;
		},
		async openSendModal(p:InboxreaderPurchase){
			let filesToAttach = await this.getFilesToAttach(p);
			this.mailTo = this.settings.UBL.BookkeeperEmailConfigs["purchases"]?.Email || "";
			if (p.PeppolOrder) {
				this.mailTo = this.settings.UBL.BookkeeperEmailConfigs["incomming-peppol-orders"]?.Email || this.mailTo;
			} else {
				this.mailTo = this.settings.UBL.BookkeeperEmailConfigs["incomming-email-documents"]?.Email || this.mailTo;
			}
			this.mailSubject = p.Subject || (filesToAttach.length > 0 ? filesToAttach[0].name : "");
			this.mailContent = p.PeppolOrder ? "" : p.NoteForBookkeeper;
			this.downloadedFiles = filesToAttach;
			this.sendMailDialogOpened = true;
		},
		async shouldContinueIfPurchaseExists(p:Purchase):Promise<boolean>{
			let possibleDuplicate = await PurchaseService.findPossibleDuplicatePurchase(p);
			if (!possibleDuplicate) return true;
			let text = this.$ct("components.purchases.this-document-is-a-possible-duplicate-of-number", {
				documentId: possibleDuplicate.ID,
				documentNr: possibleDuplicate.ComputedFriendlyID
			});
			console.log(text);
			let confirm = await this.$wf.confirm(text.toString(), this.$ct("common.go-further"), this.$ct("common.cancel"), this.$ct("common.warning"), "warning");
			return confirm == ConfirmResult.YES;
		},
		async create() {
			if (this.selectionIsMixed) {
				this.$wf.notify(this.$ct("mixed-selection"), "warning", 5000, "exclamation-triangle");
				return;
			}
			try{

				this.creating = true;
				let p = await this.state.selectedType.createPurchase();

				let shouldContinue = await this.shouldContinueIfPurchaseExists(p);
				if (!shouldContinue) {
					this.creating = false;
					return;
				}

				this.state.edit = p;
			}catch(err){

			}finally{
				this.creating = false;
			}
		},
		async toggleApproval() {
			if (this.selectionHasApprovedFiles) {
				await this.unapproveDocuments();
			} else {
				await this.approveDocuments();
			}
		},
		async approveDocuments() {
			const selection = this.state.selectedType.selection;
			for (const purchase of selection) {
				for (const file of purchase.Files) {
					await InboxreaderService.approveInboxreaderPurchaseFile(purchase.ID, file.ID);
				}
			}
			this.state.clearSelection();
			await this.state.fetchPurchases();
		},
		async unapproveDocuments() {
			const message = this.$ct("components.purchases.are-you-sure-you-want-to-unapprove-selection");
			const confirm = await this.$wf.confirm(message, this.$ct("common.yes"), this.$ct("common.no"), this.$ct("common.warning"), "warning");
			if (!confirm) return;
			const selection = this.state.selectedType.selection;
			for (const purchase of selection) {
				for (const file of purchase.Files) {
					if (file.Approved) {
						await InboxreaderService.unapproveInboxreaderPurchaseFile(purchase.ID, file.ID);
					}
				}
			}
			this.state.clearSelection();
			await this.state.fetchPurchases();
		},
		updatePeppolStatus() {
			this.dossierConfig.peppolRegistrationStatus = 1;
			this.showRegistration = false;
		},
		async rejectSelfBilledSale(){
			let entry = this.state.selectedType.selection[0];
			if (!entry) return;
			if (!entry.PeppolOrder) return;
			let text = this.$ct("are-you-sure-you-want-to-reject-document-nr", {nr: entry.PeppolOrder.OrderNumber});
			let result = await this.$wf.confirm(text, this.$ct("common.yes"), this.$ct("common.no"), this.$ct("common.warning"), "warning");
			if (!result) return;
			await InboxreaderService.archiveInboxreaderPurchase(entry.ID);
			this.state.selectedType.clearSelection();
			let i = this.state.selectedType.purchases.indexOf(entry);
			if (i == -1) return;
			this.state.selectedType.purchases.splice(i, 1);
			let docType = this.$ct("common.invoice");
			if (entry.PeppolOrder.OrderType == "SelfBilledCreditNote") {
				docType = this.$ct("common.creditnote");
			}
			text = this.$ct("let-the-customer-know-that-the-invoice-has-been-rejected", {docType});
			this.$wf.dialog(text, this.$ct("common.ok"), this.$ct("common.info"), "info");
		},
		async createSelfBilledSale(){
			let entry = this.state.selectedType.selection[0];
			if (!entry) return;
			await SaleService.fromInboxReader(entry.ID); // Check for errors
			this.$router.push(`/sales/from-inboxreader/${entry.ID}`);
		},
		activeChipStyle(label:InboxreaderLabel):Record<string, string>{
			return {
				backgroundColor: label.Color,
				borderColor: label.Color,
				color: getLabelTextColor(label.Color)
			};
		},
		async bulkAssignLabel(labelId:number){
			await this.state.applyLabelToSelection(labelId, true);
		},
		selectedPurchaseIds():number[]{
			return this.state.selectedType.selection.map(p=>p.ID);
		},
		async bulkSendToBookkeeper(){
			let ids = this.selectedPurchaseIds();
			if (!ids.length) return;
			let message = this.$ct("confirm-send-to-bookkeeper", {count: ids.length, email: this.peppolBookkeeperEmail});
			let confirm = await this.$wf.confirm(message, this.$ct("common.yes"), this.$ct("common.no"), this.$ct("common.warning"), "warning");
			if (!confirm) return;
			await InboxreaderService.batchSendToBookkeeper(ids);
			this.$wf.notify(this.$ct("send-to-bookkeeper-started"), "success", 4000, "check");
			this.state.clearSelection();
			await this.confirmArchiveAfterSend(ids);
		},
		async confirmArchiveAfterSend(ids:number[]){
			let message = this.$ct("confirm-archive-after-send", {count: ids.length});
			let confirm = await this.$wf.confirm(message, this.$ct("common.yes"), this.$ct("common.no"), this.$ct("common.info"), "info");
			if (!confirm) return;
			await this.archivePurchases(ids);
		},
		async archivePurchases(ids:number[]){
			for (let id of ids) {
				await InboxreaderService.archiveInboxreaderPurchase(id);
			}
			await this.state.fetchPurchases();
		},
		// The upload outlives this screen, so the list refreshes on the event of
		// the upload itself instead of on an event of the tile or the modal.
		async onUploadFinished(error:string){
			if (error) return;
			await this.state.fetchPurchases();
		},
		async onLabelsUpdated(){
			await this.state.loadLabels();
			// Drop filters that point at deleted labels.
			this.state.selectedLabelIds = this.state.selectedLabelIds.filter(
				id => this.state.labels.some(l=>l.ID == id)
			);
			await this.state.fetchPurchases();
		}
	},
	mounted() {
		ToolbarService.title = this.$ct("title");
	},
	destroyed() {
		InboxUpload.off("done", this.onUploadFinished);
	},
	async created() {
		InboxUpload.on("done", this.onUploadFinished);
		await this.state.doInitialFetch();
		await this.state.loadLabels();
		this.registrations = await PeppolService.getRegistrationData();
		try{
			await PeppolService.processIncommingPeppolMessages();
		}catch(err){
			let hErr = err as HttpError;
			this.$wf.notify(hErr.getText(), "danger", 3000, "exclamation-triangle");
		}
	},
	components: {
		AppToProcessFiles,
		AppInboxSplitView,
		AppEmailPreview,
		AppEditPurchase,
		AppPeppolRegistrationModal,
		AppSendMessageDialog,
		AppInboxLabelsModal,
		AppUploadPurchaseModal,
		AppUploadQueueBar,
	},
	i18nEx: {
		componentPrefix: "components.winbox"
	}
});
